import { createStyles } from '@material-ui/styles';
import classNames from 'classnames';
import * as React from 'react';
import { compose, defaultProps, lifecycle, withHandlers, withState } from 'recompose';
import { border, toCssValue } from '@topconagriculture/theme';
import { ActionIcon } from '../ActionIcon';
import { withBaseProps } from '../Utils';
const styles = (theme) => createStyles({
    root: {
        zIndex: 400,
        position: 'absolute',
        bottom: theme.space.margin.small,
        left: theme.space.margin.small,
        backgroundColor: theme.color.background.light,
        minWidth: 360,
        padding: toCssValue([25, theme.space.padding.small, theme.space.padding.small]),
        boxShadow: theme.common.shadow.default,
        borderRadius: theme.common.borderRadius.default,
    },
    currentValue: {
        marginBottom: 12,
        textAlign: 'center',
    },
    symbolContainer: {
        height: 26,
        display: 'flex',
        alignItems: 'center',
        borderRight: border(theme.color.grey.dark),
        borderLeft: border(theme.color.grey.dark),
        position: 'relative',
        marginBottom: 8,
    },
    symbolDetails: {
        position: 'absolute',
        bottom: 85,
        opacity: 0,
        backgroundColor: theme.color.background.light,
        padding: theme.space.padding.small,
        boxShadow: theme.common.shadow.default,
        borderRadius: theme.common.borderRadius.default,
        transition: 'opacity .1s ease, transform .1s ease',
        pointerEvents: 'none',
    },
    symbolDetailsActive: {
        opacity: 1,
        transform: 'translateY(-5px)',
    },
    symbolDetailsColor: {
        height: 40,
        width: 40,
        borderRadius: '50%',
        margin: `auto ${theme.space.margin.small} auto auto`,
    },
    symbolDetailsLabel: {
        lineHeight: 1.25,
    },
    symbolBar: {
        position: 'relative',
        width: '100%',
        height: 10,
        pointerEvents: 'none',
    },
    symbolPointer: {
        zIndex: 1,
        position: 'absolute',
        width: 1,
        height: '100%',
        backgroundColor: theme.color.background.dark,
        pointerEvents: 'none',
    },
    captionContainer: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'spaceBetween',
    },
    captionStart: {
        width: 'calc(100% / 3)',
        textAlign: 'left',
    },
    captionMiddle: {
        width: 'calc(100% / 3)',
        textAlign: 'center',
    },
    captionEnd: {
        width: 'calc(100% / 3)',
        textAlign: 'right',
    },
    closeIcon: {
        position: 'absolute',
        top: theme.space.margin.smallest,
        right: theme.space.margin.smallest,
        cursor: 'pointer',
    },
});
const Legend = (props) => {
    const { t, classes, legend, precision, onClose, symbolMouseEnterHandler, symbolMouseMoveHandler, symbolMouseLeaveHandler, currentItem, showDetails, pointerOffset, detailsOffset, setDetailsRef, setBarRef, testId = 'Legend', } = props;
    if (!legend || !legend.length) {
        return null;
    }
    const minValue = legend[0].min;
    const maxValue = legend[legend.length - 1].max;
    const unitOfMeasureSymbol = legend[0].unitOfMeasureSymbol;
    const gradient = legend.reduce((acc, item) => `${acc}, ${item.color}`, 'to right');
    const caption = (React.createElement("div", { className: classes.captionContainer },
        React.createElement("div", { className: classes.captionStart },
            minValue.toFixed(precision),
            " ",
            unitOfMeasureSymbol),
        React.createElement("div", { className: classes.captionMiddle },
            ((minValue + maxValue) / 2).toFixed(precision),
            " ",
            unitOfMeasureSymbol),
        React.createElement("div", { className: classes.captionEnd },
            maxValue.toFixed(precision),
            " ",
            unitOfMeasureSymbol)));
    const details = (React.createElement(React.Fragment, null,
        React.createElement("div", { ref: setDetailsRef, className: classNames(classes.symbolDetails, { [classes.symbolDetailsActive]: showDetails }), style: { left: `${detailsOffset}px` } }, currentItem && (React.createElement(React.Fragment, null,
            React.createElement("div", { className: classes.symbolDetailsColor, style: { background: currentItem.color } }),
            React.createElement("div", { className: classes.symbolDetailsLabel }, t('legend:min_label', { data: `${currentItem.min.toFixed(precision)} ${unitOfMeasureSymbol}` })),
            React.createElement("div", { className: classes.symbolDetailsLabel }, t('legend:max_label', { data: `${currentItem.max.toFixed(precision)} ${unitOfMeasureSymbol}` }))))),
        React.createElement("div", { className: classes.symbolPointer, style: { left: `${pointerOffset}px` } })));
    return (React.createElement("div", { className: classes.root, "data-testid": `${testId}` },
        React.createElement("div", { className: classes.currentValue, "data-testid": `${testId}-value` }, `${(currentItem ? currentItem.min : minValue).toFixed(precision)} ${unitOfMeasureSymbol} - ${(currentItem
            ? currentItem.max
            : maxValue).toFixed(precision)} ${unitOfMeasureSymbol}`),
        React.createElement("div", null,
            React.createElement("div", { className: classes.symbolContainer, onMouseEnter: symbolMouseEnterHandler, onMouseMove: symbolMouseMoveHandler, onMouseLeave: symbolMouseLeaveHandler },
                details,
                React.createElement("div", { ref: setBarRef, className: classes.symbolBar, style: { background: `linear-gradient(${gradient})` } })),
            caption),
        React.createElement(ActionIcon, { "data-testid": `${testId}-close`, className: classes.closeIcon, iconName: "close", onClick: onClose, iconSize: 12 })));
};
export default compose(withBaseProps({ styles }), defaultProps({
    precision: 2,
}), withState('detailsRef', 'setDetailsRef', null), withState('barRef', 'setBarRef', null), withState('currentItem', 'setCurrentItem', undefined), withState('showDetails', 'setShowDetails', false), withState('detailsOffset', 'setDetailsOffset', 0), withState('pointerOffset', 'setPointerOffset', 0), withHandlers({
    symbolMouseEnterHandler: ({ setShowDetails }) => () => {
        setShowDetails(true);
    },
    symbolMouseMoveHandler: ({ legend, barRef, detailsRef, setCurrentItem, setDetailsOffset, setPointerOffset, }) => (event) => {
        let posX = event.nativeEvent.offsetX;
        const barWidth = barRef.offsetWidth;
        if (posX <= 0) {
            posX = 1;
        }
        else if (posX >= barWidth) {
            posX = barWidth - 1;
        }
        setPointerOffset(posX);
        const detailsOffset = posX - (detailsRef.offsetWidth * posX) / barWidth;
        setDetailsOffset(detailsOffset);
        const minValue = legend[0].min;
        const maxValue = legend[legend.length - 1].max;
        const currentValue = (maxValue - minValue) * (posX / barWidth) + minValue;
        const currentItem = legend.find((item) => currentValue >= item.min && currentValue <= item.max);
        setCurrentItem(currentItem);
    },
    symbolMouseLeaveHandler: ({ setShowDetails }) => () => {
        setShowDetails(false);
    },
}), lifecycle({
    componentDidUpdate(prevProps) {
        if (prevProps.legend !== this.props.legend && this.props.legend && this.props.legend.length) {
            const minValue = this.props.legend[0].min;
            const currentItem = this.props.legend.find((item) => minValue + 1 >= item.min && minValue + 1 <= item.max);
            this.props.setCurrentItem(currentItem);
            this.props.setShowDetails(false);
            this.props.setDetailsOffset(0);
            this.props.setPointerOffset(0);
        }
    },
}))(Legend);
//# sourceMappingURL=Legend.js.map